import { Common, color } from '@stockbit/ui';
import { useState, createRef, useRef, RefObject } from 'react';
import If from 'global/ErrorHandlers/If';
import useCountdown from 'hooks/useCountdown';
import { formatCensorPhoneNumber } from 'utils/textFormatter';
import { InputNumber, InputContainer } from './styled';
import { PasswordSubmitButton } from '../styled';

const { Flex, Text } = Common;

interface ConfirmOTPProps {
  isLoading: boolean;
  onSubmit: (otp: string) => void;
  onResend?(): void;
  type: 'email' | 'phone';
  phoneNumber?: string;
}

const MAX_LENGTH = 4;
const KEY_CODE_BACKSPACE = 8;
const KEY_CODE_DELETE = 46;
const DEFAULT_CD_DURATION = 60;

const CONTENT = {
  email: 'melalui email',
  phone: 'ke nomor ',
};

function ConfirmOTP({
  isLoading,
  onSubmit,
  onResend,
  phoneNumber,
  type = 'email',
}: ConfirmOTPProps) {
  const inputRef = useRef<RefObject<HTMLInputElement>[]>(
    [...Array(MAX_LENGTH)].map(() => createRef()),
  );

  const [inputValue, setInputValue] = useState([...Array(MAX_LENGTH)]);

  const onChangeInput = (e) => (index) => {
    const { value } = e.target;
    const newInputValue = [...inputValue];

    if (e.keyCode !== KEY_CODE_BACKSPACE && e.keyCode !== KEY_CODE_DELETE) {
      // eslint-disable-next-line prefer-destructuring
      newInputValue[index] = value[0];
      setInputValue(newInputValue);

      setTimeout(() => {
        if (index < 3 && inputRef.current[index].current.value !== '') {
          inputRef.current[index + 1].current.focus();
        }
      }, 50);
    }

    if (e.keyCode === KEY_CODE_BACKSPACE) {
      newInputValue[index] = '';
      setInputValue(newInputValue);
      setTimeout(() => {
        if (index > 0 && index <= MAX_LENGTH - 1) {
          inputRef.current[index - 1].current.focus();
        }
      }, 50);
    }
  };

  const handleSubmit = async () => {
    const otp = inputValue.join('');

    onSubmit(otp);
  };

  const handleResend = async () => {
    const res = await onResend();
    return res;
  };

  const suffix =
    type === 'email' ? (
      CONTENT.email
    ) : (
      <>
        {CONTENT.phone} <b>+{formatCensorPhoneNumber(phoneNumber)}</b>
      </>
    );

  const { seconds, minutes, onEnd, isFinished } = useCountdown({
    onResend: () => {
      handleResend();
    },
    time: DEFAULT_CD_DURATION,
  });

  return (
    <>
      <Flex direction="column">
        <Text size="16px" family="bold" height="1.5">
          Masukkan Kode Verifikasi
        </Text>
        <Text mb="15px" height="1.5" color={color.highgray}>
          Kode verifikasi telah dikirim {suffix}
        </Text>
      </Flex>
      <Flex align="center" style={{ gap: 16 }} mb="15px">
        {[...Array(MAX_LENGTH)].map((_e, i) => (
          <InputContainer key={`input-token${i + 1}`}>
            <InputNumber
              ref={inputRef.current[i]}
              value={inputValue[i]}
              onChange={(e) => onChangeInput(e)(i)}
              onKeyDown={(e) => onChangeInput(e)(i)}
              id={`input-email-otp-verify-${i + 1}`}
              data-cy="confirm-otp-input-box"
            />
          </InputContainer>
        ))}
        <If condition={!isFinished}>
          <Text color={color.highgray} data-cy="confirm-otp-count-down">
            Kirim Ulang
            <If condition={!isFinished}>
              ({minutes}:{seconds})
            </If>
          </Text>
        </If>
        <If condition={isFinished}>
          <Text
            color={color.primary}
            family="bold"
            style={{ cursor: 'pointer' }}
            onClick={onEnd}
            data-cy="confirm-otp-kirim-ulang-button"
          >
            Kirim Ulang
          </Text>
        </If>
      </Flex>
      <Flex>
        <PasswordSubmitButton
          htmlType="submit"
          type="primary"
          loading={isLoading}
          onClick={handleSubmit}
          data-cy="confirm-otp-submit-button"
        >
          Submit
        </PasswordSubmitButton>
      </Flex>
    </>
  );
}

export default ConfirmOTP;
